import React from 'react';
import { Users, Server, Brain, Cpu, Sparkles } from 'lucide-react';
import HelpIconButton from '../HelpIconButton';
import type { TrustPortalScreenKey } from '../TrustPortalLayout';

interface SubProviderScopeProps {
  onNavigate: (key: TrustPortalScreenKey) => void;
}

// Tiered sub-provider list with inline jump-to-profile links. Placeholder
// data until the canonical scope document is received.
interface SubProviderRow {
  name: string;
  tier: 1 | 2 | 3;
  role: string;
  targetKey: TrustPortalScreenKey | null;
  icon: React.ElementType;
}

const ROWS: SubProviderRow[] = [
  {
    name: 'Railway',
    tier: 1,
    role: 'Production application hosting and infrastructure.',
    targetKey: 'railway',
    icon: Server,
  },
  {
    name: 'Anthropic',
    tier: 1,
    role: 'Primary large language model provider (Claude API).',
    targetKey: 'anthropic',
    icon: Brain,
  },
  {
    name: 'OpenAI',
    tier: 1,
    role: 'Secondary model provider for specific workloads.',
    targetKey: 'openai',
    icon: Cpu,
  },
  {
    name: 'Mistral',
    tier: 2,
    role: 'Optional model provider — pending full vendor onboarding.',
    targetKey: 'mistral',
    icon: Sparkles,
  },
];

const tierBadge = (tier: 1 | 2 | 3) => {
  const base = 'px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded-full';
  if (tier === 1) return `${base} bg-red-100 text-red-800`;
  if (tier === 2) return `${base} bg-amber-100 text-amber-800`;
  return `${base} bg-gray-100 text-gray-700`;
};

const SubProviderScope: React.FC<SubProviderScopeProps> = ({ onNavigate }) => {
  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-violet-100 rounded-lg">
            <Users className="w-6 h-6 text-violet-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Sub-Provider Scope</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Upstream vendors in scope for TSG&apos;s trust disclosure.
            </p>
          </div>
        </div>
        <HelpIconButton screenId="sub-provider-scope" showLabel />
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {ROWS.map((row, idx) => {
          const Icon = row.icon;
          return (
            <div
              key={row.name}
              className={`flex items-center gap-4 p-4 ${
                idx !== ROWS.length - 1 ? 'border-b border-gray-100' : ''
              } hover:bg-gray-50 transition-colors`}
            >
              <div className="p-2 bg-gray-50 rounded-lg">
                <Icon className="w-5 h-5 text-gray-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900">{row.name}</span>
                  <span className={tierBadge(row.tier)}>Tier {row.tier}</span>
                </div>
                <div className="mt-0.5 text-xs text-gray-500">{row.role}</div>
              </div>
              {row.targetKey && (
                <button
                  onClick={() => onNavigate(row.targetKey!)}
                  className="text-xs font-medium text-blue-700 hover:text-blue-800 hover:underline"
                >
                  View profile →
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-500">
        Placeholder scope list — will be replaced with the canonical Sub-Provider Trust and
        Compliance Reference content when Ryan&apos;s handoff doc lands.
      </div>
    </div>
  );
};

export default SubProviderScope;
